import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Paper, 
  useTheme, 
  CircularProgress, 
  Alert,
  ToggleButtonGroup,
  ToggleButton,
  Tab,
  Tabs,
  GlobalStyles,
  styled,
  IconButton
} from '@mui/material';
import { 
  Assessment as AssessmentIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Speed as SpeedIcon,
  FileDownload as FileDownloadIcon,
  DirectionsCar,
  HealthAndSafety
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { FiltersPanel } from '@/components/filters/FiltersPanel';
import { SummaryCard } from '@/components/dashboard/SummaryCard';
import { BarChart } from '@/components/dashboard/BarChart';
import { DistributionChart } from '@/components/dashboard/DistributionChart';
import { StackedBarChart } from '@/components/dashboard/StackedBarChart';
import { DonutChart } from '@/components/dashboard/DonutChart';
import { DashboardSummary } from '@/types/dashboard';
import { SelectedFilters } from '@/types/filters';
import { alpha } from '@mui/material/styles';
import { MiniRegionMap } from '@/components/dashboard/MiniRegionMap';
import { ChannelPerformanceBreakdown } from '@/components/dashboard/ChannelPerformanceBreakdown';
import { SubChannelPerformanceBreakdown } from '@/components/dashboard/SubChannelPerformanceBreakdown';
import { ProductPerformanceBreakdown } from '@/components/dashboard/ProductPerformanceBreakdown';
import { RegionManagerDrillDown } from '@/components/dashboard/RegionManagerDrillDown';
import { PerformanceDataProvider } from '@/contexts/PerformanceDataContext';
import { RootState } from '@/store/store';
import { 
  setSelectedRegion,
  setSelectedProduct,
  setSelectedStatus,
  setSelectedMatrixType,
  setSelectedMatrixItem,
  setShowPerformanceMatrix,
  resetMatrixStates,
  fetchChannelPerformanceBreakdownStart,
  fetchSubchannelPerformanceBreakdownStart,
  fetchProductPerformanceBreakdownStart,
  fetchProductChannelDistributionStart,
  fetchChannelProductDistributionStart,
  fetchProductStatusDistributionStart,
  fetchChannelStatusDistributionStart,
  fetchSubchannelStatusDistributionStart,
  fetchProductConversionRateStart,
  fetchChannelConversionRateStart,
  fetchSubchannelConversionRateStart
} from '@/store/slices/dashboardSlice';
import { setCurrentFilters } from '@/store/slices/filtersSlice';
import { fetchPerformanceData } from '@/store/epics/dashboardEpics';
import { fetchRegionMapStart } from '@/store/slices/regionMapSlice';

// Chola Insurance theme colors
const CHOLA_COLORS = {
  primary: '#003087', // Deep Blue
  secondary: '#E31837', // Red
  accent: '#FFB81C', // Yellow
  background: '#FFFFFF',
  text: '#333333',
  lightBlue: '#0066CC',
  grey: '#F5F5F5',
  hover: '#004AAD' // Darker blue for hover states
};

// Create a styled component for the dashboard container that removes all padding
const DashboardContainer = styled(Box)(({ theme }) => ({
  height: '100%',
  padding: '2px !important',
  paddingTop: '2px !important',
  marginTop: '-10px',
  '& .MuiBox-root': {
    paddingTop: '2px !important',
  },
  '& > *': {
    paddingTop: '2px !important',
  },
  '& .css-1sf3xto': {
    paddingTop: '2px !important',
  },
  '& [class*="css"]': {
    paddingTop: '2px !important',
  }
}));

// Define interfaces for Redux state data
interface OverallPerformance {
  total_leads: string;
  converted_leads: string;
  conversion_rate: string;
}

interface LobPerformanceItem {
  lob: string;
  total_leads: string;
  converted_leads: string;
  conversion_rate: string;
}

interface ExecutiveDashboardProps {
  dashboardData: DashboardSummary;
  loading: boolean;
  error: string | null;
  onRegionSelect: (region: string) => void;
  onProductClick: (product: string) => void;
  onStatusClick: (status: string) => void;
  onBarClick: (item: string, type: 'product' | 'channel' | 'status') => void;
  onFiltersChange: (filters: SelectedFilters) => void;
  selectedRegion?: string;
}

// Fix interface for DonutChart data
interface DonutDataItem {
  id: string;
  name: string;
  label: string;
  value: number;
  formattedValue: string;
}

// Define types for chart data
interface ChartDataItem {
  name: string;
  value: number;
}

export function ExecutiveDashboard({
  dashboardData,
  loading,
  error,
  onRegionSelect,
  onProductClick,
  onStatusClick,
  onBarClick,
  onFiltersChange,
  selectedRegion
}: ExecutiveDashboardProps) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [chartView, setChartView] = useState<'product' | 'status'>('product');
  const [distributionTabValue, setDistributionTabValue] = useState(0);
  const [conversionTabValue, setConversionTabValue] = useState(0);
  const [distributionViewMode, setDistributionViewMode] = useState<'product' | 'status'>('product');
  const [breakdownTabValue, setBreakdownTabValue] = useState(0);
  
  // Get data from Redux store instead of using local state and API calls
  const { 
    overallPerformance, 
    lobPerformance, 
    loading: reduxLoading, 
    error: reduxError,
    productChannelDistribution,
    channelProductDistribution,
    productStatusDistribution,
    channelStatusDistribution,
    subchannelStatusDistribution,
    statusProductDistribution,
    productConversionRate,
    channelConversionRate,
    subchannelConversionRate
  } = useSelector((state: RootState) => state.dashboard);

  // Get current filters from Redux store
  const { currentFilters } = useSelector((state: RootState) => state.filters);

  // Add useEffect to load missing data when component mounts or tab changes
  useEffect(() => {
    // Only dispatch if data is missing and we're not already loading
    if (!reduxLoading) {
      if (!productConversionRate) {
        dispatch(fetchProductConversionRateStart());
      }
      if (!channelConversionRate) {
        dispatch(fetchChannelConversionRateStart());
      }
      if (!subchannelConversionRate) {
        dispatch(fetchSubchannelConversionRateStart());
      }
    }
  }, [
    dispatch, 
    reduxLoading, 
    productConversionRate, 
    channelConversionRate, 
    subchannelConversionRate,
    conversionTabValue
  ]);

  // Modify the conversion rate data processing function to create properly typed data
  const processConversionRateData = () => {
    // Create empty arrays as fallbacks when data is missing
    const emptyData: DonutDataItem[] = [];
    
    // Channel conversion rate data - handle missing data gracefully
    const channelConversionData = channelConversionRate?.channels 
      ? channelConversionRate.channels.map(channel => ({
          id: channel.channel,
          name: channel.channel,
          label: channel.channel,
          value: parseFloat(channel.conversion_rate || '0'),
          formattedValue: `${channel.conversion_rate || '0'}%`
        })).sort((a, b) => b.value - a.value) // Sort by conversion rate descending
      : emptyData;

    // SubChannel conversion rate data - handle missing data gracefully
    const subChannelConversionData = subchannelConversionRate?.subchannels 
      ? subchannelConversionRate.subchannels.map(subchannel => ({
          id: subchannel.subchannel,
          name: subchannel.subchannel,
          label: subchannel.subchannel,
          value: parseFloat(subchannel.conversion_rate || '0'),
          formattedValue: `${subchannel.conversion_rate || '0'}%`
        })).sort((a, b) => b.value - a.value) // Sort by conversion rate descending
      : emptyData;

    // Product conversion rate data - handle missing data gracefully
    const productConversionData = productConversionRate?.products 
      ? productConversionRate.products.map(product => ({
          id: product.product_group,
          name: product.product_group,
          label: product.product_group,
          value: parseFloat(product.conversion_rate || '0'),
          formattedValue: `${product.conversion_rate || '0'}%`
        })).sort((a, b) => b.value - a.value) // Sort by conversion rate descending
      : emptyData;

    return {
      channelConversionData,
      subChannelConversionData,
      productConversionData
    };
  };

  // Fix the processChartData function to properly handle API types
  const processChartData = () => {
    if (!productChannelDistribution || !channelProductDistribution) return null;

    // Process data for product and channel distribution
    const productChannelData = productChannelDistribution.products.map(product => {
      // Check if channels is an object or array and process accordingly
      let channelsArray: ChartDataItem[] = [];
      
      // If channels is an object (from Redux data), convert to array
      if (product.channels && typeof product.channels === 'object' && !Array.isArray(product.channels)) {
        channelsArray = Object.entries(product.channels).map(([channelName, channelData]) => {
          return {
            name: channelName,
            value: parseInt(typeof channelData === 'object' && channelData !== null && 'total_leads' in channelData
              ? (channelData.total_leads as string || '0')
              : '0'),
            conversion_rate: typeof channelData === 'object' && channelData !== null ? 
              ('conversion_rate' in channelData ? channelData.conversion_rate as string : '0') : '0',
            converted_leads: typeof channelData === 'object' && channelData !== null ? 
              ('converted_leads' in channelData ? channelData.converted_leads as string : '0') : '0',
            contribution_rate: typeof channelData === 'object' && channelData !== null ? 
              ('contribution_rate' in channelData ? channelData.contribution_rate as string : '0') : '0'
          };
        });
      } 
      // If channels is already an array (as per the ProductChannelItem interface)
      else if (Array.isArray(product.channels)) {
        channelsArray = product.channels.map(ch => ({
          name: ch.channel,
          value: ch.count,
          conversion_rate: ch.conversion_rate,
          converted_leads: ch.converted_leads,
          contribution_rate: ch.contribution_rate
        }));
      }
      
      // Use optional chaining and type checking for property access
      const productName = 'product_group' in product 
        ? (product.product_group as string || '') 
        : ('product' in product ? (product.product as string || '') : '');
      
      const totalLeads = 'total_leads' in product 
        ? parseInt(product.total_leads as string || '0') 
        : ('total' in product ? (product.total as number || 0) : 0);


      
      return {
        name: productName,
        total: totalLeads,
        channels: channelsArray,
        conversion_rate: product.conversion_rate,
        converted_leads: product.converted_leads,
        contribution_rate: product.contribution_rate
      };
    });

    // Process data for status distribution (using channel product data)
    const statusData = channelProductDistribution.channels.map(channel => {
      // Check if products is an object or array and process accordingly
      let productsArray: ChartDataItem[] = [];
      
      // If products is an object (from Redux data), convert to array
      if (channel.products && typeof channel.products === 'object' && !Array.isArray(channel.products)) {
        productsArray = Object.entries(channel.products).map(([productName, productData]) => ({
          name: productName,
          value: parseInt(typeof productData === 'object' && productData !== null && 'total_leads' in productData 
            ? (productData.total_leads as string || '0') 
            : '0'),
            conversion_rate: typeof productData === 'object' && productData !== null ? 
              ('conversion_rate' in productData ? productData.conversion_rate as string : '0') : '0',
            converted_leads: typeof productData === 'object' && productData !== null ? 
              ('converted_leads' in productData ? productData.converted_leads as string : '0') : '0'
        }));
      } 
      // If products is already an array (as per the ChannelProductItem interface)
      else if (Array.isArray(channel.products)) {
        productsArray = channel.products.map(prod => ({
          name: prod.product,
          value: prod.count,
          conversion_rate: prod.conversion_rate,
          converted_leads: prod.converted_leads
        }));
      }
      
      // Use optional chaining and type checking for property access
      const totalLeads = 'total_leads' in channel 
        ? parseInt(channel.total_leads as string || '0') 
        : ('total' in channel ? (channel.total as number || 0) : 0);
      
      return {
        name: channel.channel,
        total: totalLeads,
        channels: productsArray,
        conversion_rate: channel.conversion_rate,
        converted_leads: channel.converted_leads
      };
    });

    return {
      productChannelData,
      statusData
    };
  };

  // Process distribution chart data based on the selected tab
  const processDistributionData = () => {
    // Check if any of the required data is missing
    const hasProductStatusData = !!productStatusDistribution;
    const hasChannelStatusData = !!channelStatusDistribution;
    const hasSubchannelStatusData = !!subchannelStatusDistribution;
    const hasStatusProductData = !!statusProductDistribution;
    
    // Create a default empty array for missing data
    const emptyData = [{ name: "No Data", value: 0, products: [] }];
    
    // Process data for Product Status Distribution - restructure from API response
    const productStatusData = hasProductStatusData && productStatusDistribution?.products 
      ? productStatusDistribution.products.flatMap(product => {
          if (!product.statuses || typeof product.statuses !== 'object') {
            return [];
          }
          
          // Convert the statuses object to an array of status items with products
          return Object.entries(product.statuses).map(([statusKey, statusData]) => {
            return {
              name: statusData.status || statusKey,
              value: parseInt(statusData.total_leads || '0'),
              products: [{ 
                name: product.product_group, 
                value: parseInt(statusData.total_leads || '0') 
              }]
            };
          });
        })
      : emptyData;

    // Process data for Channel Status Distribution
    const channelStatusData = hasChannelStatusData && channelStatusDistribution?.channels
      ? channelStatusDistribution.channels.flatMap(channel => {
          if (!channel.statuses || typeof channel.statuses !== 'object') {
            return [];
          }
          
          // Convert the statuses object to an array of status items with channels
          return Object.entries(channel.statuses).map(([statusKey, statusData]) => {
            return {
              name: statusData.status || statusKey,
              value: parseInt(statusData.total_leads || '0'),
              products: [{ 
                name: channel.channel, 
                value: parseInt(statusData.total_leads || '0') 
              }]
            };
          });
        })
      : emptyData;

    // Process data for SubChannel Status Distribution
    const subChannelStatusData = hasSubchannelStatusData && subchannelStatusDistribution?.subchannels
      ? subchannelStatusDistribution.subchannels.flatMap(subchannel => {
          if (!subchannel.statuses || typeof subchannel.statuses !== 'object') {
            return [];
          }
          
          // Convert the statuses object to an array of status items with subchannels
          return Object.entries(subchannel.statuses).map(([statusKey, statusData]) => {
            return {
              name: statusData.status || statusKey,
              value: parseInt(statusData.total_leads || '0'),
              products: [{ 
                name: subchannel.subchannel, 
                value: parseInt(statusData.total_leads || '0') 
              }]
            };
          });
        })
      : emptyData;

    // Restructure the data to combine by status for each view
    const restructureByStatus = (dataArray: any[]) => {
      // Group by status name
      const statusGroups = dataArray.reduce((groups: {[key: string]: any}, item) => {
        if (!groups[item.name]) {
          groups[item.name] = {
            name: item.name,
            value: 0,
            products: []
          };
        }
        groups[item.name].value += item.value;
        groups[item.name].products = [...groups[item.name].products, ...(item.products || [])];
        return groups;
      }, {});
      
      return Object.values(statusGroups);
    };

    return {
      productStatusData: restructureByStatus(productStatusData),
      channelStatusData: restructureByStatus(channelStatusData),
      subChannelStatusData: restructureByStatus(subChannelStatusData)
    };
  };

  const handleChartViewChange = (
    event: React.MouseEvent<HTMLElement>,
    newView: 'product' | 'status' | null,
  ) => {
    if (newView !== null) {
      setChartView(newView);
    }
  };

  const handleBarClick = (item: string, type: 'product' | 'channel' | 'status') => {
    if (type === 'product') {
      onProductClick(item);
    } else {
      onBarClick(item, type);
    }
  };

  const handleDistributionBarClick = (status: string, product: string) => {
    if (distributionTabValue === 0) {
      onBarClick(product, 'channel');
    } else if (distributionTabValue === 2) {
      onProductClick(product);
    }
  };

  // Handle distribution tab change
  const handleDistributionTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setDistributionTabValue(newValue);
  };

  // Handle conversion tab change
  const handleConversionTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setConversionTabValue(newValue);
  };

  // Toggle Distribution chart view between 'by product' and 'by status'
  const handleDistributionViewChange = (
    event: React.MouseEvent<HTMLElement>,
    newView: 'product' | 'status' | null,
  ) => {
    if (newView !== null) {
      setDistributionViewMode(newView);
    }
  };

  // Handle breakdown tab change
  const handleBreakdownTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setBreakdownTabValue(newValue);
  };

  // Handle export for the currently active breakdown table
  const handleBreakdownExport = () => {
    let exportBtnId;
    
    switch(breakdownTabValue) {
      case 0:
        exportBtnId = 'channel-performance-export-btn';
        break;
      case 1:
        exportBtnId = 'subchannel-performance-export-btn';
        break;
      case 2:
        exportBtnId = 'product-performance-export-btn';
        break;
      case 3:
        exportBtnId = 'manager-performance-export-btn';
        break;
      default:
        return;
    }
    
    const exportButton = document.getElementById(exportBtnId);
    if (exportButton) {
      exportButton.click();
    }
  };

  // Render the distribution chart based on the active tab
  const renderDistributionChart = () => {
    if (!dashboardData) return <CircularProgress />;

    const distributionData = processDistributionData();
    
    if (!distributionData) return <CircularProgress />;

    let activeData;
    let title;

    switch(distributionTabValue) {
      case 0:
        activeData = distributionData.channelStatusData;
        title = "Channel Status Distribution";
        break;
      case 1:
        activeData = distributionData.subChannelStatusData;
        title = "SubChannel Status Distribution";
        break;
      case 2:
        activeData = distributionData.productStatusData;
        title = "Product Status Distribution";
        break;
      default:
        activeData = distributionData.channelStatusData;
        title = "Channel Status Distribution";
    }

    if (!activeData || activeData.length === 0) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2" color="text.secondary">No data available</Typography>
        </Box>
      );
    }

    return (
      <StackedBarChart
        title={title}
        data={activeData}
        onBarClick={handleDistributionBarClick}
        compact={true}
        viewBy={distributionViewMode}
      />
    );
  };

  // Render the conversion rate chart based on the active tab
  const renderConversionRateChart = () => {
    // Show loading state while waiting for data
    if (reduxLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <CircularProgress />
        </Box>
      );
    }

    if (conversionTabValue === 0 && !channelConversionRate) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2" color="text.secondary">Channel conversion data not available</Typography>
        </Box>
      );
    }
    
    if (conversionTabValue === 1 && !subchannelConversionRate) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2" color="text.secondary">Subchannel conversion data not available</Typography>
        </Box>
      );
    }
    
    if (conversionTabValue === 2 && !productConversionRate) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2" color="text.secondary">Product conversion data not available</Typography>
        </Box>
      );
    }

    const conversionData = processConversionRateData();
    
    if (!conversionData) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2" color="text.secondary">Failed to process conversion data</Typography>
        </Box>
      );
    }

    let activeData;
    let title;

    switch(conversionTabValue) {
      case 0:
        activeData = conversionData.channelConversionData;
        title = "Conversion % by Channel";
        break;
      case 1:
        activeData = conversionData.subChannelConversionData;
        title = "Conversion % by SubChannel";
        break;
      case 2:
        activeData = conversionData.productConversionData;
        title = "Conversion % by Product";
        break;
      default:
        activeData = conversionData.channelConversionData;
        title = "Conversion % by Channel";
    }
    
    // Check if the active data has any values
    if (!activeData || activeData.length === 0) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2" color="text.secondary">No data available for selected view</Typography>
        </Box>
      );
    }
    
    return (
      <Box sx={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <DonutChart
          title={title}
          data={activeData}
          compact={true}
        />
      </Box>
    );
  };

  // Render the summary cards for overall performance
  const renderSummaryCards = () => {
    // Custom mini summary card styled specifically for the executive dashboard
    const MiniSummaryCard = ({ 
      title, 
      value, 
      icon, 
      subValue = null,
      subTitle = null,
      loading = false,
      valueColor = CHOLA_COLORS.primary
    }: { 
      title: string, 
      value: string | number, 
      icon: React.ReactNode,
      subValue?: string | number | null,
      subTitle?: string | null,
      loading?: boolean,
      valueColor?: string
    }) => (
      <Box
        sx={{
          p: 0.75,
          bgcolor: 'white',
          borderRadius: 1,
          boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
          border: `1px solid ${alpha(CHOLA_COLORS.grey, 0.7)}`,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          justifyContent: 'center',
          textAlign: 'center'
        }}
      >
        {loading ? (
          <CircularProgress size={24} sx={{ alignSelf: 'center', my: 0.5 }} />
        ) : (
          <>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: valueColor,
                fontSize: '1rem',
                lineHeight: 1,
                mb: 0.25
              }}
            >
              {typeof value === 'number' ? value.toLocaleString() : value}
            </Typography>
            
            {subValue && subTitle && (
              <Box sx={{ mt: 0.5, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0.5 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: CHOLA_COLORS.text,
                    fontWeight: 500,
                    fontSize: '0.65rem'
                  }}
                >
                  {subTitle}:
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: valueColor,
                    fontWeight: 600,
                    fontSize: '0.65rem'
                  }}
                >
                  {subValue}
                </Typography>
              </Box>
            )}
            
            <Typography
              variant="caption"
              sx={{
                color: CHOLA_COLORS.text,
                fontWeight: 500,
                fontSize: '0.65rem'
              }}
            >
              <b>{title}</b>
            </Typography>
          </>
        )}
      </Box>
    );

    const loadingOrError = reduxLoading || !overallPerformance;

    return (
      <Grid container spacing={0.5}>
        <Grid item xs={4}>
          <MiniSummaryCard
            title="Total Base"
            value={loadingOrError ? '—' : parseInt(overallPerformance?.total_leads || '0').toLocaleString()}
            icon={<PeopleIcon />}
            loading={reduxLoading}
          />
        </Grid>
        
        <Grid item xs={4}>
          <MiniSummaryCard
            title="Converted NOP"
            value={loadingOrError ? '—' : parseInt(overallPerformance?.converted_leads || '0').toLocaleString()}
            icon={<TrendingUpIcon />}
            loading={reduxLoading}
          />
        </Grid>
        
        <Grid item xs={4}>
          <MiniSummaryCard
            title="Conversion %"
            value={loadingOrError ? '—' : overallPerformance?.conversion_rate || '0%'}
            icon={<SpeedIcon />}
            loading={reduxLoading}
            valueColor={getConversionRateColor(overallPerformance?.conversion_rate || '0%')}
          />
        </Grid>

        {/* Dynamically render LOB cards */}
        {lobPerformance && lobPerformance.length > 0 && (
          <Grid item xs={12} sx={{ mt: 0.5 }}>
            <Grid container spacing={0.5}>
              {lobPerformance.map((lob, index) => {
                // Helper function to get appropriate icon for LOB
                const getLobIcon = (lobName: string) => {
                  switch(lobName.toLowerCase()) {
                    case 'motor':
                      return <DirectionsCar />;
                    case 'non-motor':
                      return <HealthAndSafety />;
                    default:
                      // Use generic icon for other LOB types
                      return <AssessmentIcon />;
                  }
                };
                
                // Helper function to get color based on LOB
                const getLobColor = (lobName: string) => {
                  switch(lobName.toLowerCase()) {
                    case 'motor':
                      return CHOLA_COLORS.lightBlue;
                    case 'non-motor':
                      return CHOLA_COLORS.secondary;
                    default:
                      // Calculate dynamic color to ensure distinct colors for different LOBs
                      const colors = [CHOLA_COLORS.lightBlue, CHOLA_COLORS.secondary, CHOLA_COLORS.accent, '#4CAF50', '#9C27B0'];
                      return colors[index % colors.length];
                  }
                };
                
                return (
                  <Grid item xs={12 / Math.min(lobPerformance.length, 3)} key={lob.lob}>
                    <MiniSummaryCard
                      title={lob.lob}
                      value={`${parseInt(lob.converted_leads || '0').toLocaleString()} / ${parseInt(lob.total_leads || '0').toLocaleString()}`}
                      subValue={lob.conversion_rate || '—'}
                      subTitle="Conv. Rate"
                      icon={getLobIcon(lob.lob)}
                      loading={reduxLoading}
                      valueColor={getLobColor(lob.lob)}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </Grid>
        )}
      </Grid>
    );
  };

  // Helper function to determine color based on conversion rate
  const getConversionRateColor = (rateStr: string): string => {
    const rate = parseFloat(rateStr.replace('%', ''));
    if (rate >= 15) return '#4CAF50'; // Green for high conversion
    if (rate >= 8) return '#FF9800'; // Orange for medium conversion
    return CHOLA_COLORS.secondary; // Red for low conversion
  };

  // Handling filter changes from the FiltersPanel
  const handleLocalFiltersChange = (filters: SelectedFilters) => {
    // Call the parent onFiltersChange callback
    onFiltersChange(filters);
    
    // Update Redux store with the new filters 
    dispatch(setCurrentFilters({
      ...filters,
      selectedMonth: filters.selectedMonth ? [parseInt(filters.selectedMonth)] : undefined,
      selectedYear: filters.selectedYear ? [parseInt(filters.selectedYear)] : undefined
    }));
    
    // Update performance data with new filters
    dispatch(fetchPerformanceData(filters));
    
    // Update region map with new filters 
    dispatch(fetchRegionMapStart(filters));
    
    // Fetch breakdowns with new filters
    dispatch(fetchProductPerformanceBreakdownStart(filters));
    dispatch(fetchChannelPerformanceBreakdownStart(filters));
    dispatch(fetchSubchannelPerformanceBreakdownStart(filters));
    
    // Fetch distribution data with filter arguments
    dispatch(fetchProductChannelDistributionStart());
    dispatch(fetchChannelProductDistributionStart());
    dispatch(fetchProductStatusDistributionStart());
    dispatch(fetchChannelStatusDistributionStart());
    dispatch(fetchSubchannelStatusDistributionStart());
    
    // Fetch conversion rate data
    dispatch(fetchProductConversionRateStart());
    dispatch(fetchChannelConversionRateStart());
    dispatch(fetchSubchannelConversionRateStart());
  };

  return (
    <>
      <GlobalStyles
        styles={{
          '#executive-dashboard': {
            paddingTop: '2px !important',
          },
          '#executive-dashboard .MuiBox-root': {
            paddingTop: '2px !important',
          },
          '.css-1sf3xto': {
            paddingTop: '2px !important'
          }
        }}
      />
      <DashboardContainer 
        id="executive-dashboard" 
        sx={{ 
          pt: '2px !important',
          paddingTop: '2px !important',
          '.css-1sf3xto': {
            paddingTop: '2px !important'
          }
        }}
      >
        

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : (
          <>
            {/* First Row: Overall Performance and Total Base By Product and Channel */}
            <Grid container spacing={1} sx={{ mb: 0, mt: 0 }}>
              {/* Overall Performance */}
              <Grid item xs={12} md={4}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 1,
                    borderRadius: 1,
                    boxShadow: `0 0 8px ${alpha(CHOLA_COLORS.primary, 0.1)}`,
                    border: `1px solid ${alpha(CHOLA_COLORS.grey, 0.7)}`,
                    height: '100%'
                  }}
                >
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      mb: 0.5,
                      fontWeight: 600,
                      color: CHOLA_COLORS.primary,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      fontSize: '0.9rem'
                    }}
                  >
                    <SpeedIcon sx={{ color: CHOLA_COLORS.secondary, fontSize: '1rem' }} />
                    Overall Performace
                  </Typography>
                  
                  {renderSummaryCards()}
                </Paper>
              </Grid>

              {/* Total Base by Product and Channel */}
              <Grid item xs={12} md={8}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 0.75,
                    borderRadius: 1,
                    boxShadow: `0 0 8px ${alpha(CHOLA_COLORS.primary, 0.1)}`,
                    border: `1px solid ${alpha(CHOLA_COLORS.grey, 0.7)}`,
                    height: 'auto',
                    minHeight: '165px'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.25 }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 600,
                        color: CHOLA_COLORS.primary,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        fontSize: '0.9rem'
                      }}
                    >
                      <AssessmentIcon sx={{ color: CHOLA_COLORS.secondary, fontSize: '1rem' }} />
                      Total Base By {chartView === 'product' ? 'Product' : 'Status'}
                    </Typography>
                    
                    <Box sx={{ bgcolor: '#f5f5f5', borderRadius: 1, p: 0.5 }}>
                      <ToggleButtonGroup
                        size="small"
                        value={chartView}
                        exclusive
                        onChange={handleChartViewChange}
                        aria-label="chart view"
                      >
                        <ToggleButton 
                          value="product" 
                          aria-label="product view"
                          sx={{ 
                            px: 1, 
                            py: 0.25,
                            fontSize: '0.7rem',
                            fontWeight: 500,
                            '&.Mui-selected': {
                              bgcolor: 'white',
                              color: CHOLA_COLORS.primary,
                              fontWeight: 600,
                              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                            }
                          }}
                        >
                          Product
                        </ToggleButton>
                        <ToggleButton 
                          value="status" 
                          aria-label="status view"
                          sx={{ 
                            px: 1, 
                            py: 0.25,
                            fontSize: '0.7rem',
                            fontWeight: 500,
                            '&.Mui-selected': {
                              bgcolor: 'white',
                              color: CHOLA_COLORS.primary,
                              fontWeight: 600,
                              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                            }
                          }}
                        >
                          Channel
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </Box>
                  </Box>
                  
                  
                  <Box sx={{ height: '150px' }}>
                    <BarChart
                      data={chartView === 'product' 
                        ? (processChartData()?.productChannelData || []) 
                        : (processChartData()?.statusData || [])}
                      onBarClick={handleBarClick}
                      compact={true}
                      ultraCompact={true}
                      showXAxis={true}
                    />
                  </Box>
                </Paper>
              </Grid>
            </Grid>

            {/* Second Row placeholder - will be implemented next */}
            <Grid container spacing={2} sx={{ mb: 2, mt: 0 }}>
              {/* Base distribution analysis */}
              <Grid item xs={12} md={4}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 1,
                    boxShadow: `0 0 8px ${alpha(CHOLA_COLORS.primary, 0.1)}`,
                    border: `1px solid ${alpha(CHOLA_COLORS.grey, 0.7)}`,
                    height: '300px'
                  }}
                >
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      mb: 1.5,
                      fontWeight: 600,
                      color: CHOLA_COLORS.primary,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      fontSize: '1rem'
                    }}
                  >
                    Base distribution analysis
                  </Typography>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Box sx={{ flex: 1 }}>
                      <Tabs
                        value={distributionTabValue}
                        onChange={handleDistributionTabChange}
                        sx={{
                          minHeight: 'auto',
                          '& .MuiTab-root': {
                            py: 0.5,
                            minHeight: 'auto',
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            width: '33.33%',
                            minWidth: '80px'
                          },
                          '& .MuiTabs-indicator': {
                            backgroundColor: CHOLA_COLORS.primary,
                            height: '2px',
                            width: '33.33% !important',
                            left: `${distributionTabValue * 33.33}% !important`
                          },
                          '& .MuiTabs-flexContainer': {
                            justifyContent: 'space-between'
                          }
                        }}
                      >
                        <Tab label="Channel" />
                        <Tab label="SubChannel" />
                        <Tab label="Product" />
                      </Tabs>
                    </Box>
                    
                    <Box sx={{ bgcolor: '#f5f5f5', borderRadius: 1, p: 0.5, ml: 1 }}>
                      <ToggleButtonGroup
                        size="small"
                        value={distributionViewMode}
                        exclusive
                        onChange={handleDistributionViewChange}
                        aria-label="distribution view"
                      >
                        <ToggleButton 
                          value="product" 
                          aria-label="by product"
                          sx={{ 
                            px: 1, 
                            py: 0.25,
                            fontSize: '0.7rem',
                            fontWeight: 500,
                            '&.Mui-selected': {
                              bgcolor: 'white',
                              color: CHOLA_COLORS.primary,
                              fontWeight: 600,
                              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                            }
                          }}
                        >
                          Product
                        </ToggleButton>
                        {/* <ToggleButton 
                          value="status" 
                          aria-label="by status"
                          sx={{ 
                            px: 1, 
                            py: 0.25,
                            fontSize: '0.7rem',
                            fontWeight: 500,
                            '&.Mui-selected': {
                              bgcolor: 'white',
                              color: CHOLA_COLORS.primary,
                              fontWeight: 600,
                              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                            }
                          }}
                        >
                          Status
                        </ToggleButton> */}
                      </ToggleButtonGroup>
                    </Box>
                  </Box>
                  
                  <Box sx={{ p: 1, height: 'calc(100% - 80px)' }}>
                    {renderDistributionChart()}
                  </Box>
                </Paper>
              </Grid>

              {/* India Map */}
              <Grid item xs={12} md={4}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 1,
                    pt: 1, 
                    pb: 1,
                    borderRadius: 1,
                    boxShadow: `0 0 8px ${alpha(CHOLA_COLORS.primary, 0.1)}`,
                    border: `1px solid ${alpha(CHOLA_COLORS.grey, 0.7)}`,
                    height: '300px',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      mb: 0.5,
                      fontWeight: 600,
                      color: CHOLA_COLORS.primary,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      fontSize: '1rem',
                      height: '24px'
                    }}
                  >
                    Regional Distribution
                  </Typography>
                  
                  <Box sx={{ 
                    flex: 1,
                    height: 'calc(100% - 24px)', 
                    px: 0, 
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    <MiniRegionMap
                      regionData={dashboardData?.regionData || {}}
                      onRegionSelect={onRegionSelect}
                      compact={true}
                      selectedRegion={selectedRegion}
                    />
                  </Box>
                </Paper>
              </Grid>

              {/* Conversion % */}
              <Grid item xs={12} md={4}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 1,
                    boxShadow: `0 0 8px ${alpha(CHOLA_COLORS.primary, 0.1)}`,
                    border: `1px solid ${alpha(CHOLA_COLORS.grey, 0.7)}`,
                    height: '300px',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 600,
                        color: CHOLA_COLORS.primary,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        fontSize: '1rem',
                        minWidth: '120px'
                      }}
                    >
                      Conversion %
                    </Typography>
                    
                    <Tabs
                      value={conversionTabValue}
                      onChange={handleConversionTabChange}
                      sx={{
                        minHeight: 'auto',
                        flex: 1,
                        '& .MuiTab-root': {
                          py: 0.5,
                          minHeight: 'auto',
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          width: '33.33%',
                          minWidth: '80px'
                        },
                        '& .MuiTabs-indicator': {
                          backgroundColor: CHOLA_COLORS.primary,
                          height: '2px',
                          width: '33.33% !important',
                          left: `${conversionTabValue * 33.33}% !important`
                        },
                        '& .MuiTabs-flexContainer': {
                          justifyContent: 'space-between'
                        }
                      }}
                    >
                      <Tab label="Channel" />
                      <Tab label="SubChannel" />
                      <Tab label="Product" />
                    </Tabs>
                  </Box>
                  
                  <Box sx={{ 
                    flex: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: 'calc(100% - 50px)',
                    width: '100%',
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    {renderConversionRateChart()}
                  </Box>
                </Paper>
              </Grid>
            </Grid>

            {/* Third Row - Breakdown */}
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    pt: 1.5,
                    pb: 1.5,
                    borderRadius: 1,
                    boxShadow: `0 0 8px ${alpha(CHOLA_COLORS.primary, 0.1)}`,
                    border: `1px solid ${alpha(CHOLA_COLORS.grey, 0.7)}`
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 600,
                        color: CHOLA_COLORS.primary,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        fontSize: '1rem',
                        minWidth: '180px'
                      }}
                    >
                      Performance Breakdown
                    </Typography>
                    
                    <Tabs
                      value={breakdownTabValue}
                      onChange={handleBreakdownTabChange}
                      sx={{
                        minHeight: 'auto',
                        flex: 1,
                        '& .MuiTab-root': {
                          py: 0.5,
                          minHeight: 'auto',
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          width: 'auto',
                          minWidth: '80px',
                          flex: 'none'
                        },
                        '& .MuiTabs-indicator': {
                          backgroundColor: CHOLA_COLORS.primary,
                          height: '2px'
                        },
                        '& .MuiTabs-flexContainer': {
                          justifyContent: 'flex-start',
                          gap: '20px'
                        }
                      }}
                    >
                      <Tab label="Channel" />
                      {/* <Tab label="SubChannel" /> */}
                      {/* <Tab label="Product" /> */}
                      <Tab label="Manager" />
                    </Tabs>
                    
                    <IconButton 
                      onClick={handleBreakdownExport}
                      title="Export data"
                      size="small"
                      sx={{ color: CHOLA_COLORS.primary }}
                    >
                      <FileDownloadIcon />
                    </IconButton>
                  </Box>
                  
                  <Box>
                    {/* Channel tab content */}
                    {breakdownTabValue === 0 && (
                      <ChannelPerformanceBreakdown compact={true} />
                    )}
                    
                    {/* SubChannel tab content */}
                    {/* {breakdownTabValue === 1 && (
                      <SubChannelPerformanceBreakdown compact={true} />
                    )} */}
                    
                    {/* Product tab content */}
                    {/* {breakdownTabValue === 1 && (
                      <ProductPerformanceBreakdown compact={true} />
                    )} */}
                    
                    {/* Manager tab content */}
                    {breakdownTabValue === 1 && (
                      <div>
                      <RegionManagerDrillDown 
                        region="ALL" 
                        filters={{
                          selectedMonths: currentFilters?.selectedMonth,
                          selectedYears: currentFilters?.selectedYear,
                          selectedLOB: currentFilters?.selectedLOB,
                          selectedProducts: currentFilters?.selectedProducts,
                          selectedChannels: currentFilters?.selectedChannels,
                          selectedSubChannels: currentFilters?.selectedSubChannels,
                          selectedRegion: currentFilters?.selectedRegion?.[0], // Convert array to single string
                          selectedSubProducts: currentFilters?.selectedSubProducts
                        }}
                        compact={true} 
                      />
                      </div>
                    )}
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </>
        )}
      </DashboardContainer>
    </>
  );
} 